import React from 'react';
import { SmallText } from '@ui-library/typography';
import { Form as AntForm, FormProps as AntFormProps } from 'antd';
import styled from 'styled-components';
import { Flex } from '@ui-library/flex';

// Re-export with antd's native `variant` omitted — wrapper uses `variant` for the 'next' style.
// `any` mirrors antd's own `FormProps<Values = any>` default; narrowing breaks consumer
// onFinish/onValuesChange signatures that pass a concrete Values type.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type FormProps<Values = any> = Omit<AntFormProps<Values>, 'variant'> & {
  variant?: 'next';
};
export type { FormListFieldData, FormListProps } from 'antd/es/form';
export type { FormInstance } from 'antd/es/form/Form';
export type { FormRule } from 'antd';

const OptionalText = styled(SmallText)`
  color: ${({ theme }) => theme.token.colorTextSecondary};
  font-style: italic;
`;

const customizeRequiredMark = (label: React.ReactNode, { required }: { required: boolean }) => (
  <Flex gap=".5rem" alignItems="center">
    {label}
    {!required && <OptionalText>(Optional)</OptionalText>}
  </Flex>
);

const FormList = styled(AntForm.List)``;

// Translate the wrapper's `variant` prop to a transient `$variant` styled prop
// so it isn't forwarded to antd — antd 5.23+ typed its own `variant` prop with
// a different enum ('outlined' | 'filled' | 'borderless' | 'underlined') that
// would clash with our 'next' value.
const StyledForm = styled(AntForm).attrs<{ variant?: 'next' }>(({ variant, requiredMark }) => ({
  $variant: variant,
  requiredMark: requiredMark ?? customizeRequiredMark,
}))<{ $variant?: 'next' }>`
  .ant-form-item-label {
    padding-bottom: ${({ $variant }) => ($variant ? 0 : 'unset')};
  }

  .ant-form-item-label label {
    font-weight: 500;
    font-size: ${({ theme, $variant }) =>
      $variant ? theme.token.fontSize : theme.token.fontSizeLG}px;
    height: auto !important;
  }
`;

// Cast the styled component to `React.FC<FormProps>` so consumer `FormInstance<MyValues>`
// props are accepted — styled-components v5 collapses AntForm's <Values> generic. The
// runtime value is still the styled-component (isStyledComponent returns true), which
// matters for tests that spread Form into a mock.
export const Form = StyledForm as unknown as React.FC<FormProps> & {
  Item: typeof AntForm.Item;
  List: typeof FormList;
  useForm: typeof AntForm.useForm;
  useWatch: typeof AntForm.useWatch;
  useFormInstance: typeof AntForm.useFormInstance;
  Provider: typeof AntForm.Provider;
  ErrorList: typeof AntForm.ErrorList;
};

Form.Item = AntForm.Item;
Form.List = FormList;
Form.useForm = AntForm.useForm;
Form.useWatch = AntForm.useWatch;
Form.useFormInstance = AntForm.useFormInstance;
Form.Provider = AntForm.Provider;
Form.ErrorList = AntForm.ErrorList;
